import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { PkpDatePipe } from 'angular/custom/pipes/pkp-date.pipe';
import { ConfirmModal } from 'angular/custom/services/confirm-modal.service';
import { ErrorHandlerService } from 'angular/custom/services/error-handler.service';
import { Growl } from 'angular/custom/services/growl.service';
import {
    MultiYearAuctionsResource,
    MultiYearCfoAuctionGuiDto
} from 'angular/resources/multi-year-auctions.resource';
import { Decimal } from 'decimal.js';
import { BehaviorSubject } from 'rxjs';

type FormState = 'unchanged' | 'review' | 'submitted';

@Component({
    selector: 'multi-year-bid-editor',
    template: require('./multi-year-bid-editor.component.html')
})
export class MultiYearBidEditorComponent implements OnInit, OnChanges {
    @Input() auction: MultiYearCfoAuctionGuiDto;
    @Output() bidChanged = new EventEmitter<MultiYearCfoBidDto>();
    @Input() bidToEdit: MultiYearCfoBidDto;
    @Output() cancelEdit = new EventEmitter();
    @Output('formState') formStateEventEmitter = new BehaviorSubject<FormState>(
        'unchanged'
    );

    @ViewChild('bidForm') bidForm: NgForm;

    bid: MultiYearCfoBidDto;
    gtcConfirmation0: boolean = false;
    gtcConfirmation1: boolean = false;
    possibleStartYears: { label: number; value: Date }[];
    possibleEndYears: { label: number; value: Date }[];
    formState: FormState = 'unchanged';
    loading = false;

    get submitBidButtonValue() {
        if (this.formState === 'unchanged') {
            return (this.bid.bidId != null ? 'Update' : 'Submit') + ' bid';
        } else if (this.formState === 'review') {
            return 'Confirm bid';
        } else if (this.formState === 'submitted') {
            return 'Add bid';
        } else {
            return 'Submit';
        }
    }

    constructor(
        private confirmModal: ConfirmModal,
        private pkpDatePipe: PkpDatePipe,
        private multiYearAuctionsResource: MultiYearAuctionsResource,
        private growl: Growl,
        private errorHandlerService: ErrorHandlerService
    ) {
        this.bid = this.createInitialBid();
    }

    private createInitialBid() {
        return {
            runtime: {},
            amount: { value: { value: null }, unit: 'Cm³/h' }
        } as MultiYearCfoBidDto;
    }

    ngOnInit(): void {
        this.possibleStartYears = this.getYearsBetween(
            (this.auction.runtime.start as Date).getFullYear() - 1,
            (this.auction.runtime.end as Date).getFullYear()
        );
        this.bid.runtime.start = this.possibleStartYears[0].value;
        this.adjustPossibleRuntimeEndYears();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['bidToEdit'] != null &&
            changes['bidToEdit'].currentValue != null &&
            changes['bidToEdit'].currentValue.bidId != null
        ) {
            const preFillBidToEdit = () => {
                this.formState = 'unchanged';
                this.formStateEventEmitter.next(this.formState);

                this.bid = { ...this.bidToEdit };
                this.bid.runtime = { ...this.bidToEdit.runtime };
                this.bid.amount = { ...this.bidToEdit.amount };
                this.bid.amount.value = { ...this.bidToEdit.amount.value };
                this.bid.amount.value.value = new Decimal(
                    this.bidToEdit.amount.value.value
                );
                this.ngOnInit();
                // reset runtime as onInit overwrites it
                this.bid.runtime = { ...this.bidToEdit.runtime };
                Object.keys(this.bidForm.controls).forEach(
                    (controlKey: string) =>
                        (
                            this.bidForm.controls[controlKey] as FormControl
                        ).markAsTouched()
                );
            };

            !this.bidForm.submitted && this.bidForm.dirty
                ? this.confirmModal
                      .fromText(
                          'Your current bid has not been saved. Do you really want to proceed?'
                      )
                      .subscribe(preFillBidToEdit, () => this.cancelEdit.emit())
                : preFillBidToEdit();
        }
    }

    adjustPossibleRuntimeEndYears() {
        this.possibleEndYears = this.getYearsBetween(
            this.bid.runtime.start != null
                ? (this.bid.runtime.start as Date).getFullYear()
                : (this.auction.runtime.start as Date).getFullYear(),
            (this.auction.runtime.end as Date).getFullYear() + 1
        );
        if (
            !this.bid.runtime.end ||
            !this.possibleEndYears.some(
                (val) => val.value.valueOf() === this.bid.runtime.end.valueOf()
            )
        ) {
            this.bid.runtime.end =
                this.possibleEndYears[this.possibleEndYears.length - 1].value;
        }
    }

    private getYearsBetween(fromYear: number, toYear: number) {
        const possibleStartYears: { label: number; value: Date }[] = [];

        for (let i = fromYear + 1; i < toYear; i++) {
            possibleStartYears.push({
                label: i,
                value: moment(`${i}-10-01 09:00:00`).toDate()
            });
        }

        return possibleStartYears;
    }

    public placeBid() {
        if (this.formState === 'unchanged') {
            this.formState = 'review';
            this.formStateEventEmitter.next(this.formState);
            return;
        }
        if (this.formState === 'submitted') {
            this.formState = 'unchanged';
            this.formStateEventEmitter.next(this.formState);
            this.cancel();

            return;
        }
        this.loading = true;
        if (this.bid.bidId != null) {
            this.multiYearAuctionsResource
                .updateBid(this.auction.id, this.bid.bidId, this.bid)
                .subscribe(
                    (res) => {
                        this.growl.show('Successfully updated bid.', 'success');
                        this.formState = 'submitted';
                        this.formStateEventEmitter.next(this.formState);
                        this.bidChanged.emit(res);
                        this.loading = false;
                    },
                    (err) => {
                        this.loading = false;
                        this.errorHandlerService.handle(
                            err,
                            'Bid could not be updated.'
                        );
                    }
                );
        } else {
            this.multiYearAuctionsResource
                .placeBid(this.auction.id, this.bid)
                .subscribe(
                    (res) => {
                        this.formState = 'submitted';
                        this.formStateEventEmitter.next(this.formState);
                        this.growl.show('Successfully placed bid.', 'success');
                        this.bidChanged.emit(res);
                        this.loading = false;
                    },
                    (err) => {
                        this.loading = false;
                        this.errorHandlerService.handle(
                            err,
                            'Bid could not be placed.'
                        );
                    }
                );
        }
    }

    back() {
        this.formState = 'unchanged';
        this.formStateEventEmitter.next(this.formState);
    }

    cancel() {
        this.formState = 'unchanged';
        this.formStateEventEmitter.next(this.formState);
        this.cancelEdit.emit();
        this.bid = this.createInitialBid();
        this.ngOnInit();
        this.bidForm.form.markAsPristine();
        this.bidForm.controls['amount'].reset();
        this.bidForm.controls['gtcConfirmation0'].reset();
        if (this.bidForm.controls['gtcConfirmation1']) {
            this.bidForm.controls['gtcConfirmation1'].reset();
        }
    }

    public compareDates(d1: any, d2: any): boolean {
        if (!d1 || !d2) {
            // do not want null to equal null for this
            return false;
        }
        return d1.valueOf() === d2.valueOf();
    }
}
